import React, { useEffect } from 'react';
import { Card } from '../components/ui/card';
import Header from '../components/Header';
import { useLanguage } from '../context/LanguageContext';
import { Train, Bus, Car, Plane, Clock, Banknote, MapPin, Info, ExternalLink } from 'lucide-react';

const DeineAnreise = () => {
  const { language } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const translations = {
    de: {
      title: 'Deine Anreise',
      subtitle: 'So erreichst du Pranburi / Sam Roi Yot',
      intro: 'Die Anreise zu unserer Villa ist unkompliziert. Von Bangkok aus gibt es mehrere komfortable Optionen – wähle die, die am besten zu dir passt.',
      
      // Train
      trainTitle: 'Mit dem Zug',
      trainDesc: 'Eine der schönsten Arten zu reisen. Züge fahren vom neuen Hauptbahnhof Bangkok (Krung Thep Aphiwat Central Terminal) ab, der bequem an das MRT-Netz angeschlossen ist.',
      trainDuration: 'ca. 4 Stunden bis Hua Hin',
      trainPrice: 'ab ca. 200 THB (ohne Klimaanlage) bzw. 300-500 THB (mit Klimaanlage)',
      trainTip: 'Tipp: Von Hua Hin aus Taxi zur Villa (ca. 1.200 THB, ca. 1 Stunde)',
      trainBooking: 'Tickets & Fahrplan',
      trainNote: 'Online-Tickets sind ca. 250 THB teurer, garantieren aber den Sitzplatz.',
      
      // Minivan
      minivanTitle: 'Mit dem Minivan',
      minivanDesc: 'Die beliebteste Option! Klimatisierte Minibusse fahren regelmäßig von verschiedenen Terminals in Bangkok ab. Besonders praktisch: Einige fahren direkt bis Sam Roi Yot.',
      minivanDuration: 'ca. 3-3,5 Stunden',
      minivanPrice: 'ca. 300 THB',
      minivanTip: 'Insider-Tipp: Minibusse mit Krokodil-Logo (Mo Chit Terminal, Gebäude D, Schalter 11) fahren direkt bis zur Unterkunft in Sam Roi Yot!',
      minivanDepartures: 'Abfahrtsorte: Mo Chit, Southern Terminal, Khao San Road, Ekkamai',
      
      // Bus
      busTitle: 'Mit dem Bus vom Flughafen',
      busDesc: 'Direkte Busse vom Flughafen Suvarnabhumi nach Hua Hin. Komfortabel und ohne Umsteigen in der Stadt.',
      busDuration: 'ca. 3,5-4 Stunden',
      busPrice: 'ca. 300-400 THB',
      busTip: 'Ideal wenn du direkt vom Flughafen anreist',
      
      // Taxi
      taxiTitle: 'Mit dem Taxi / Privattransfer',
      taxiDesc: 'Die bequemste Option mit Tür-zu-Tür-Service. Besonders lohnenswert für Gruppen oder wenn du viel Gepäck hast.',
      taxiDuration: 'ca. 3-4 Stunden (je nach Verkehr)',
      taxiPrice: 'ca. 3.500-4.500 THB (Festpreis)',
      taxiTip: 'Günstiger wenn vorab über lokale Anbieter gebucht – wir helfen gerne!',
      taxiAirportTip: 'Am Flughafen: Nimm ein Taxi von der Abflug-Ebene (oben) statt Ankunft – keine Warteschlangen!',
      
      // Transfer offer
      transferTitle: 'Unser Flughafentransfer',
      transferDesc: 'Wir bieten einen komfortablen Abholservice vom Flughafen Bangkok (BKK oder DMK) direkt zur Villa an. Preis und Details auf Anfrage bei der Buchung.',
      
      // General info
      infoTitle: 'Gut zu wissen',
      info1: 'Von Hua Hin zur Villa sind es noch ca. 45-60 Minuten mit dem Taxi',
      info2: 'Pranburi liegt ca. 250 km südlich von Bangkok',
      info3: 'Die Reise führt durch wunderschöne Landschaften mit Reisfeldern und Küstenblick',
      info4: 'Die beste Reisezeit ist November bis April',
      
      distanceTitle: 'Entfernungen',
      fromBangkok: 'Von Bangkok',
      fromHuaHin: 'Von Hua Hin',
      fromBangkokDist: 'ca. 250 km (3-4 Stunden)',
      fromHuaHinDist: 'ca. 45 km (45-60 Minuten)',
      
      moreInfo: 'Mehr Informationen'
    },
    en: {
      title: 'Getting Here',
      subtitle: 'How to reach Pranburi / Sam Roi Yot',
      intro: 'Getting to our villa is straightforward. From Bangkok, there are several comfortable options – choose the one that suits you best.',
      
      trainTitle: 'By Train',
      trainDesc: 'One of the most scenic ways to travel. Trains depart from Bangkok\'s new main station (Krung Thep Aphiwat Central Terminal), conveniently connected to the MRT network.',
      trainDuration: 'approx. 4 hours to Hua Hin',
      trainPrice: 'from approx. 200 THB (non-AC) or 300-500 THB (with AC)',
      trainTip: 'Tip: From Hua Hin, take a taxi to the villa (approx. 1,200 THB, about 1 hour)',
      trainBooking: 'Tickets & Schedule',
      trainNote: 'Online tickets cost about 250 THB more but guarantee your seat.',
      
      minivanTitle: 'By Minivan',
      minivanDesc: 'The most popular option! Air-conditioned minibuses run regularly from various terminals in Bangkok. Especially convenient: Some go directly to Sam Roi Yot.',
      minivanDuration: 'approx. 3-3.5 hours',
      minivanPrice: 'approx. 300 THB',
      minivanTip: 'Insider tip: Minibuses with crocodile logo (Mo Chit Terminal, Building D, Counter 11) go directly to accommodations in Sam Roi Yot!',
      minivanDepartures: 'Departure points: Mo Chit, Southern Terminal, Khao San Road, Ekkamai',
      
      busTitle: 'By Airport Bus',
      busDesc: 'Direct buses from Suvarnabhumi Airport to Hua Hin. Comfortable and without changing in the city.',
      busDuration: 'approx. 3.5-4 hours',
      busPrice: 'approx. 300-400 THB',
      busTip: 'Ideal if arriving directly from the airport',
      
      taxiTitle: 'By Taxi / Private Transfer',
      taxiDesc: 'The most comfortable option with door-to-door service. Especially worthwhile for groups or with lots of luggage.',
      taxiDuration: 'approx. 3-4 hours (depending on traffic)',
      taxiPrice: 'approx. 3,500-4,500 THB (fixed price)',
      taxiTip: 'Cheaper when booked in advance through local providers – we\'re happy to help!',
      taxiAirportTip: 'At the airport: Take a taxi from Departures level (upstairs) instead of Arrivals – no queues!',
      
      transferTitle: 'Our Airport Transfer',
      transferDesc: 'We offer comfortable pickup service from Bangkok Airport (BKK or DMK) directly to the villa. Price and details available on request when booking.',
      
      infoTitle: 'Good to Know',
      info1: 'From Hua Hin to the villa is about 45-60 minutes by taxi',
      info2: 'Pranburi is about 250 km south of Bangkok',
      info3: 'The journey passes through beautiful landscapes with rice fields and coastal views',
      info4: 'Best travel time is November to April',
      
      distanceTitle: 'Distances',
      fromBangkok: 'From Bangkok',
      fromHuaHin: 'From Hua Hin',
      fromBangkokDist: 'approx. 250 km (3-4 hours)',
      fromHuaHinDist: 'approx. 45 km (45-60 minutes)',
      
      moreInfo: 'More Information'
    },
    th: {
      title: 'การเดินทาง',
      subtitle: 'วิธีเดินทางมาถึงปราณบุรี / สามร้อยยอด',
      intro: 'การเดินทางมาวิลล่าของเราสะดวกสบาย จากกรุงเทพฯ มีหลายทางเลือก เลือกแบบที่เหมาะกับคุณที่สุด',
      
      trainTitle: 'โดยรถไฟ',
      trainDesc: 'หนึ่งในวิธีการเดินทางที่สวยงามที่สุด รถไฟออกจากสถานีกลางกรุงเทพอภิวัฒน์ ซึ่งเชื่อมต่อกับ MRT อย่างสะดวก',
      trainDuration: 'ประมาณ 4 ชั่วโมงถึงหัวหิน',
      trainPrice: 'เริ่มต้นประมาณ 200 บาท (ไม่มีแอร์) หรือ 300-500 บาท (มีแอร์)',
      trainTip: 'เคล็ดลับ: จากหัวหินนั่งแท็กซี่มาวิลล่า (ประมาณ 1,200 บาท, ประมาณ 1 ชั่วโมง)',
      trainBooking: 'ตั๋วและตารางเวลา',
      trainNote: 'ตั๋วออนไลน์แพงกว่าประมาณ 250 บาท แต่รับประกันที่นั่ง',
      
      minivanTitle: 'โดยรถตู้',
      minivanDesc: 'ตัวเลือกยอดนิยม! รถตู้ปรับอากาศวิ่งประจำจากสถานีต่างๆ ในกรุงเทพฯ บางเส้นวิ่งตรงไปสามร้อยยอด',
      minivanDuration: 'ประมาณ 3-3.5 ชั่วโมง',
      minivanPrice: 'ประมาณ 300 บาท',
      minivanTip: 'เคล็ดลับ: รถตู้โลโก้จระเข้ (หมอชิต อาคาร D เคาน์เตอร์ 11) วิ่งตรงถึงที่พักในสามร้อยยอด!',
      minivanDepartures: 'จุดขึ้นรถ: หมอชิต, สถานีขนส่งใต้, ถนนข้าวสาร, เอกมัย',
      
      busTitle: 'โดยรถบัสจากสนามบิน',
      busDesc: 'รถบัสตรงจากสนามบินสุวรรณภูมิไปหัวหิน สะดวกสบายไม่ต้องต่อรถ',
      busDuration: 'ประมาณ 3.5-4 ชั่วโมง',
      busPrice: 'ประมาณ 300-400 บาท',
      busTip: 'เหมาะมากถ้าเดินทางจากสนามบินโดยตรง',
      
      taxiTitle: 'โดยแท็กซี่ / รถรับส่งส่วนตัว',
      taxiDesc: 'ตัวเลือกที่สะดวกสบายที่สุด บริการรับส่งถึงประตู คุ้มค่าโดยเฉพาะสำหรับกลุ่มหรือมีกระเป๋ามาก',
      taxiDuration: 'ประมาณ 3-4 ชั่วโมง (ขึ้นอยู่กับการจราจร)',
      taxiPrice: 'ประมาณ 3,500-4,500 บาท (ราคาเหมา)',
      taxiTip: 'ถูกกว่าถ้าจองล่วงหน้าผ่านผู้ให้บริการท้องถิ่น - เรายินดีช่วยเหลือ!',
      taxiAirportTip: 'ที่สนามบิน: ขึ้นแท็กซี่จากชั้นผู้โดยสารขาออก (ชั้นบน) แทนขาเข้า - ไม่ต้องรอคิว!',
      
      transferTitle: 'บริการรับส่งสนามบิน',
      transferDesc: 'เรามีบริการรับส่งสะดวกสบายจากสนามบินกรุงเทพฯ (BKK หรือ DMK) มาถึงวิลล่าโดยตรง สอบถามราคาและรายละเอียดได้เมื่อจอง',
      
      infoTitle: 'ข้อมูลที่ควรรู้',
      info1: 'จากหัวหินถึงวิลล่าประมาณ 45-60 นาทีโดยแท็กซี่',
      info2: 'ปราณบุรีอยู่ห่างจากกรุงเทพฯ ประมาณ 250 กม. ไปทางใต้',
      info3: 'การเดินทางผ่านทิวทัศน์สวยงามของทุ่งนาและวิวชายฝั่ง',
      info4: 'ช่วงเวลาที่ดีที่สุดคือพฤศจิกายน - เมษายน',
      
      distanceTitle: 'ระยะทาง',
      fromBangkok: 'จากกรุงเทพฯ',
      fromHuaHin: 'จากหัวหิน',
      fromBangkokDist: 'ประมาณ 250 กม. (3-4 ชั่วโมง)',
      fromHuaHinDist: 'ประมาณ 45 กม. (45-60 นาที)',
      
      moreInfo: 'ข้อมูลเพิ่มเติม'
    }
  };

  const txt = translations[language] || translations.de;

  const TransportCard = ({ icon: Icon, title, description, duration, price, tip, extra, color }) => (
    <Card className="p-6 border-amber-200 hover:shadow-lg transition-shadow duration-300">
      <div className="flex items-start gap-4">
        <div className={`p-3 rounded-full ${color} flex-shrink-0`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-xl font-semibold text-gray-800 mb-2">{title}</h3>
          <p className="text-gray-600 mb-4">{description}</p>
          
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Clock className="w-4 h-4 text-amber-600" />
              <span className="text-gray-700">{duration}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Banknote className="w-4 h-4 text-green-600" />
              <span className="text-gray-700">{price}</span>
            </div>
          </div>
          
          {tip && (
            <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-amber-800">{tip}</p>
              </div>
            </div>
          )}
          
          {extra && (
            <p className="mt-3 text-sm text-gray-500">{extra}</p>
          )}
        </div>
      </div>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      <Header />
      
      <main className="container mx-auto px-4 py-12 max-w-4xl pt-28">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-8 text-center">{txt.title}</h1>

        {/* Distance Overview */}
        <Card className="p-6 mb-8 bg-gradient-to-r from-amber-500 to-orange-500 text-white">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            {txt.distanceTitle}
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white/20 rounded-lg p-4">
              <p className="font-medium">{txt.fromBangkok}</p>
              <p className="text-lg">{txt.fromBangkokDist}</p>
            </div>
            <div className="bg-white/20 rounded-lg p-4">
              <p className="font-medium">{txt.fromHuaHin}</p>
              <p className="text-lg">{txt.fromHuaHinDist}</p>
            </div>
          </div>
        </Card>

        {/* Transport Options */}
        <div className="space-y-6 mb-8">
          <TransportCard
            icon={Bus}
            title={txt.minivanTitle}
            description={txt.minivanDesc}
            duration={txt.minivanDuration}
            price={txt.minivanPrice}
            tip={txt.minivanTip}
            extra={txt.minivanDepartures}
            color="bg-blue-500"
          />
          
          <TransportCard
            icon={Train}
            title={txt.trainTitle}
            description={txt.trainDesc}
            duration={txt.trainDuration}
            price={txt.trainPrice}
            tip={txt.trainTip}
            extra={txt.trainNote}
            color="bg-purple-500"
          />
          
          <TransportCard
            icon={Plane}
            title={txt.busTitle}
            description={txt.busDesc}
            duration={txt.busDuration}
            price={txt.busPrice}
            tip={txt.busTip}
            color="bg-cyan-500"
          />
          
          <TransportCard
            icon={Car}
            title={txt.taxiTitle}
            description={txt.taxiDesc}
            duration={txt.taxiDuration}
            price={txt.taxiPrice}
            tip={txt.taxiTip}
            extra={txt.taxiAirportTip}
            color="bg-green-500"
          />
        </div>

        {/* Our Transfer Service */}
        <Card className="p-6 mb-8 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
          <h2 className="text-xl font-semibold text-green-800 mb-3 flex items-center gap-2">
            <Car className="w-5 h-5" />
            {txt.transferTitle}
          </h2>
          <p className="text-green-700">{txt.transferDesc}</p>
        </Card>

        {/* Good to Know */}
        <Card className="p-6 mb-8 border-amber-200">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Info className="w-5 h-5 text-amber-600" />
            {txt.infoTitle}
          </h2>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <span className="text-amber-500 mt-1">•</span>
              <span className="text-gray-700">{txt.info1}</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-amber-500 mt-1">•</span>
              <span className="text-gray-700">{txt.info2}</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-amber-500 mt-1">•</span>
              <span className="text-gray-700">{txt.info3}</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-amber-500 mt-1">•</span>
              <span className="text-gray-700">{txt.info4}</span>
            </li>
          </ul>
        </Card>

        {/* External Links */}
        <Card className="p-6 border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">{txt.moreInfo}</h2>
          <div className="space-y-3">
            <a 
              href="https://www.thailandtrains.com/buy-train-tickets-from-bangkok-to-hua-hin/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              <span>{txt.trainBooking} (Thailand Trains)</span>
            </a>
            <a 
              href="https://www.visitsamroiyot.com/getting-to-sam-roi-yot/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              <span>Visit Sam Roi Yot - Travel Guide</span>
            </a>
          </div>
        </Card>
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8 mt-12">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-400">© 2025 Villa Thailand. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default DeineAnreise;
